import { type MouseEvent } from "react";
import { type Table as TanStackTable } from "@tanstack/react-table";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

type PageItem = number | "start-ellipsis" | "end-ellipsis";

interface DataTablePaginationProps<TData> {
  className?: string;
  table: TanStackTable<TData>;
}

const PAGE_SIBLING_COUNT = 1;
const VISIBLE_PAGES_WITHOUT_ELLIPSIS = 6;

const createPageRange = (start: number, end: number) =>
  end < start ? [] : Array.from({ length: end - start + 1 }, (_, index) => start + index);

const createPageItems = (currentPage: number, totalPages: number): PageItem[] => {
  if (totalPages <= VISIBLE_PAGES_WITHOUT_ELLIPSIS) {
    return createPageRange(1, totalPages);
  }

  const currentPageNumber = currentPage + 1;
  const visiblePages = new Set<number>([1, totalPages]);

  createPageRange(
    Math.max(1, currentPageNumber - PAGE_SIBLING_COUNT),
    Math.min(totalPages, currentPageNumber + PAGE_SIBLING_COUNT)
  ).forEach(pageNumber => visiblePages.add(pageNumber));

  if (currentPageNumber <= PAGE_SIBLING_COUNT + 2) {
    createPageRange(2, Math.min(4, totalPages - 1)).forEach(pageNumber =>
      visiblePages.add(pageNumber)
    );
  }

  if (currentPageNumber >= totalPages - PAGE_SIBLING_COUNT - 1) {
    createPageRange(Math.max(2, totalPages - 3), totalPages - 1).forEach(pageNumber =>
      visiblePages.add(pageNumber)
    );
  }

  return Array.from(visiblePages)
    .sort((pageA, pageB) => pageA - pageB)
    .reduce<PageItem[]>((items, pageNumber) => {
      const previousItem = items[items.length - 1];

      if (typeof previousItem === "number") {
        const gap = pageNumber - previousItem;

        if (gap === 2) {
          items.push(previousItem + 1);
        } else if (gap > 2) {
          items.push(pageNumber < currentPageNumber ? "start-ellipsis" : "end-ellipsis");
        }
      }

      items.push(pageNumber);
      return items;
    }, []);
};

const DataTablePagination = <TData,>({ className, table }: DataTablePaginationProps<TData>) => {
  const currentPage = table.getState().pagination.pageIndex;
  const totalPages = Math.max(1, table.getPageCount());
  const canGoPrevious = table.getCanPreviousPage();
  const canGoNext = table.getCanNextPage();
  const pageItems = createPageItems(currentPage, totalPages);

  const onClickPage = (event: MouseEvent<HTMLAnchorElement>, nextPageIndex: number) => {
    event.preventDefault();

    if (nextPageIndex === currentPage || nextPageIndex < 0 || nextPageIndex >= totalPages) {
      return;
    }

    table.setPageIndex(nextPageIndex);
  };

  return (
    <div
      className={cn(
        "flex justify-center border-border border-t px-4 py-3 text-muted-foreground text-sm",
        className
      )}
    >
      <Pagination>
        <PaginationContent className="flex-wrap justify-center">
          <PaginationItem>
            <PaginationPrevious
              href="#"
              disabled={!canGoPrevious}
              onClick={event => onClickPage(event, currentPage - 1)}
            />
          </PaginationItem>
          {pageItems.map(pageItem =>
            typeof pageItem === "number" ? (
              <PaginationItem key={pageItem}>
                <PaginationLink
                  href="#"
                  isActive={pageItem === currentPage + 1}
                  onClick={event => onClickPage(event, pageItem - 1)}
                >
                  {pageItem}
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={pageItem}>
                <PaginationEllipsis />
              </PaginationItem>
            )
          )}
          <PaginationItem>
            <PaginationNext
              href="#"
              disabled={!canGoNext}
              onClick={event => onClickPage(event, currentPage + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export { DataTablePagination };
